import { SphereSupportLink } from "@components/common/sphere-support-link";
import { ContactSupportLocations } from "@custom-types/types";
import { Banner, Text } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useRouteMatch } from "@hooks/use-route-match";
import { Box } from "@mui/material";
import { SettingsTabs } from "@router/route-params";
import { generateSettingRoute, useAppParams } from "@router/router-helper";
import { CONSTRAINT_TYPE_WARNING_WHITELIST } from "@src/constants/subscription-constants";
import { selectQuotasSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  BannerIdentifier,
  dismissBanner,
  shouldShowBanner,
} from "@utils/banner-dismissal-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo, useState } from "react";

/** Banner to show when a subscription limit is exceeded */
export function ExceededSubscriptionLimitsBanner(): JSX.Element | null {
  const routeIdentifier = useRouteMatch();
  const { trackEvent } = useTrackEvent();
  const { companyId } = useAppParams();
  const [isDismissed, setIsDismissed] = useState(false);
  const { navigateToSettingsPage } = useAppNavigation();
  const { canViewSubscriptionDetails } = useHasUserValidRoleCompanyLevel();
  const selectQuotas = useAppSelector(selectQuotasSelector);

  const subscriptionTabRoute = generateSettingRoute(
    companyId ?? "",
    SettingsTabs.subscription
  );

  const isAnyWhitelistedLimitExceeded = useMemo(() => {
    return selectQuotas.some((quota) => {
      return (
        CONSTRAINT_TYPE_WARNING_WHITELIST.includes(quota.identifier) &&
        quota.current > quota.limit
      );
    });
  }, [selectQuotas]);

  /**
   * Determines if the banner should be shown
   * Checks local storage to see if banner was dismissed recently (within 1 week)
   * It should be hidden on the subscription expired page
   */
  const shouldDisplayBanner = useMemo(() => {
    const isNotDismissedRecently = shouldShowBanner(
      BannerIdentifier.ConstraintsLimit
    );

    return (
      routeIdentifier !== "subscriptionExpired" &&
      isAnyWhitelistedLimitExceeded &&
      !isDismissed &&
      isNotDismissedRecently &&
      canViewSubscriptionDetails
    );
  }, [
    routeIdentifier,
    isAnyWhitelistedLimitExceeded,
    isDismissed,
    canViewSubscriptionDetails,
  ]);

  const handleSubscriptionNavigation = useCallback(() => {
    trackEvent({
      name: WorkspaceEvents.viewSubscriptionPage,
    });
    navigateToSettingsPage({
      settingsTab: SettingsTabs.subscription,
      companyId: companyId ?? "",
    });
  }, [trackEvent, navigateToSettingsPage, companyId]);

  const handleBannerClose = useCallback(() => {
    dismissBanner(BannerIdentifier.ConstraintsLimit);
    setIsDismissed(true);
  }, []);

  if (
    !shouldDisplayBanner ||
    window.location.pathname === subscriptionTabRoute
  ) {
    return null;
  }

  return (
    <Box data-testid="exceed-subscription-limit-banner">
      <Banner
        variant="warning"
        action={{
          label: "View More",
          action: handleSubscriptionNavigation,
        }}
        onClose={handleBannerClose}
        title="Plan limit exceeded"
      >
        <Text variant={"bodyM"}>
          Your account currently exceeds the limits of your plan. Review which
          features you can reduce or contact{" "}
          <SphereSupportLink
            location={ContactSupportLocations.exceededSubscriptionLimitsBanner}
          />{" "}
          to upgrade your plan.
        </Text>
      </Banner>
    </Box>
  );
}
